import {
  useLoadingStore,
  selectLoading,
} from '@/modules/shared/stores/loadingStore'
import moment from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { VehicleCalendarModel } from '@/app/models/vehicle/calendar/vehicleCalendarModel'
import { VehicleCalendarDayModel } from '@/app/models/vehicle/calendar/vehicleCalendarDayModel'

import { Slot } from '@/app/constants/slot'
import { getGpsEventType } from '@/app/services/gps/gpsEventTypeResolver'

import { getTmsType } from '@/app/services/tms/tmsTypeResolver'
import { getTmsRouteType } from '@/app/services/tms/tmsRouteTypeResolver'
import { mapToActivityInfo } from '@/app/mappers/activityMappers'

const useVehicleActivityService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading) 
  const mapFields = (data: any): VehicleCalendarDayModel => {
    if (data == null) {
      return null
    }

    // Convert activityDayLocalTimeDate to Moment if it's a string
    let activityDayLocalTimeDate = data.activityDayLocalTimeDate
    if (
      activityDayLocalTimeDate &&
      typeof activityDayLocalTimeDate === 'string'
    ) {
      activityDayLocalTimeDate = moment(activityDayLocalTimeDate)
    } else if (
      activityDayLocalTimeDate &&
      !moment.isMoment(activityDayLocalTimeDate)
    ) {
      activityDayLocalTimeDate = moment(activityDayLocalTimeDate)
    }

    return {
      activityDayLocalTimeDate: activityDayLocalTimeDate,
      gpsEventType: getGpsEventType(data.gpsEventType),
      beyondScope: data.beyondScope,
      dayNumber: data.dayNumber,
      driverCardReadEvent: data.driverCardReadEvent,
      tachographReadEvent: data.tachographReadEvent,
      hasAnalogTachographData: data.hasAnalogTachographData,
      hasGpsData: data.hasGpsData,
      rawGpsData: data.rawGpsData,
      hasTmsData: data.hasTmsData,
      rawTmsData:
        data.rawTmsData != null
          ? data.rawTmsData.map((r) => ({
              id: r.id,
              docNumber: r.docNumber,
              loadingDate: r.loadingDate,
              unloadingDate: r.unloadingDate,
              loadingCountry: r.loadingCountry,
              unloadingCountry: r.unloadingCountry,
              routeType: getTmsRouteType(r.routeType),
            }))
          : null,
      tmsType: getTmsType(data.tmsType),
      isHoliday: data.isHoliday,
      gpsDrivers: data.gpsDrivers,
      relatedDrivers: data.relatedDrivers,
      driverCard1Acitivities: data.driverCard1Acitivities,
      driverCard2Acitivities: data.driverCard2Acitivities,
      tachographAcitivities: data.tachographAcitivities,
      // driverCard1Acitivities:
      //   data.driverCard1Acitivities &&
      //   data.driverCard1Acitivities.map((a: MergedActivitySource) => ({
      //     source: getActivitySource(a.source),
      //     count: a.count,
      //   })),
      // driverCard2Acitivities:
      //   data.driverCard2Acitivities &&
      //   data.driverCard2Acitivities.map((a: MergedActivitySource) => ({
      //     source: getActivitySource(a.source),
      //     count: a.count,
      //   })),
      // tachographAcitivities:
      //   data.tachographAcitivities &&
      //   data.tachographAcitivities.map((a: MergedActivitySource) => ({
      //     source: getActivitySource(a.source),
      //     count: a.count,
      //   })),
      restAllDay: data.restAllDay,
    }
  }
  const assignActivitiesToDriver = async ({
    companyId,
    selectedDates,
    selectedDriverId,
    selectedVehicleId,
    dateFrom,
    dateTo,
    year,
  }: {
    companyId: string
    dateFrom: string
    dateTo: string
    year: number
    selectedDates: string[] | null
    selectedDriverId: number
    selectedVehicleId: string
  }) => {
    const stopLoading = startLoading()
    try {
      await axiosInstance.post(
        `/api/${companyId}/vehicleCalendar/assignActivitiesToDriver`,
        {
          selectedDates,
          selectedDriverId,
          selectedVehicleId,
          dateFrom,
          dateTo,
          year,
        },
      )
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }
  const assignBorderCrossingsToSelectedDriver = async ({
    companyId,
    selectedVehicleId,
    selectedDriverId,
    selectedDates,
    dateFrom,
    dateTo,
  }: {
    companyId: string
    selectedVehicleId: number | string
    selectedDriverId: number
    selectedDates: string[] | null
    dateFrom: string | null
    dateTo: string | null
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/vehicleCalendar/assignBorderCrossingsToSelectedDriver`,
        {
          selectedVehicleId,
          selectedDriverId,
          selectedDates,
          dateFrom,
          dateTo,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const assignTmsDataToSelectedDriver = async ({
    companyId,
    selectedVehicleId,
    selectedDriverId,
    selectedDates,
    dateFrom,
    dateTo,
  }: {
    companyId: string
    selectedVehicleId: number | string
    selectedDriverId: number
    selectedDates: string[] | null
    dateFrom: string | null
    dateTo: string | null
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/vehicleCalendar/assignTmsDataToSelectedDriver`,
        {
          selectedVehicleId,
          selectedDriverId,
          selectedDates,
          dateFrom,
          dateTo,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const getVehicleCalendar = async ({
    companyId,
    vehicleId,
    year,
  }: {
    companyId: string
    vehicleId: string
    year: number
  }): Promise<VehicleCalendarModel> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/vehicleCalendar/${vehicleId}/${year}`,
      )
      // Extract dayActivities from response - handle both wrapped and direct formats
      const data = res.data?.data || res.data
      const dayActivities = data?.dayActivities || []

      const calendarModel: VehicleCalendarModel = {
        days: [],
        hasData: data?.hasData || false,
      }

      // Transform each row (month) of days
      for (const row of dayActivities) {
        const daysInMonth: Array<VehicleCalendarDayModel> = row.map((c) =>
          mapFields(c),
        )
        calendarModel.days.push(daysInMonth)
      }

      return calendarModel
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const getVehicleActivity = async ({
    companyId,
    vehicleId,
    startTime,
    endTime,
    slot,
  }: {
    companyId: string
    vehicleId: string
    startTime: string
    endTime: string
    slot: Slot
  }) => {
    const stopLoading = startLoading()
    try {
      // Convert Slot enum to number for API call
      const slotNumber = typeof slot === 'number' ? slot : (slot as number)
      const res = await axiosInstance.get(
        `/api/${companyId}/activity/vehicle?&vehicleId=${vehicleId}&startTime=${startTime}&endTime=${endTime}&slot=${slotNumber}`,
      )
      const data = res.data
      if (!data) {
        return data
      }
      // Times come from API as UTC; convert to local like driver activities.
      const mapped = mapToActivityInfo(data)
      return {
        ...data,
        ...mapped,
        activities: (mapped.activities || []).map((a) => ({
          ...a,
          isVehicleSource: true,
        })),
      }
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const getSpeedInfoDays = async ({
    companyId,
    vehicleId,
    year,
  }: {
    companyId: string
    vehicleId: string
    year: number
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/speed/speedInfoDays?vehicleId=${vehicleId}&year=${year}`,
      )
      const data = res.data?.data || res.data
      // Extract days from the response - the API returns an object with days as keys
      const days = data?.days ? Object.keys(data.days) : []
      return days
    } catch (err) {
      console.error('Error fetching speed info days:', err)
      return []
    } finally {
      stopLoading()
    }
  }

  const editVehicleActivity = async ({
    companyId,
    ...request
  }: {
    companyId: string
    vehicleId: number
    activityId: number
    originalStartTime: string
    type: number
    startTime: string
    endTime: string
  }) => {
    const stopLoading = startLoading()
    try {
      await axiosInstance.post(`/api/${companyId}/activity/vehicle/edit`, request)
    } finally {
      stopLoading()
    }
  }

  return {
    getVehicleCalendar,
    getVehicleActivity,
    getSpeedInfoDays,
    editVehicleActivity,
    assignActivitiesToDriver,
    assignBorderCrossingsToSelectedDriver,
    assignTmsDataToSelectedDriver,
  }
}

export default useVehicleActivityService
